import IdeaProperty from "@/types/IdeaProperty"
import {IdentifierBlock, identifierBlocker} from "@/utils/propertyDefinition"
import {IDENTIFIER_SEPARATOR} from "@/utils/ontosUtils"

/**
 * As propriedades de uma coleção como o design as arruma: a caixa do identificador e a das demais,
 * cada uma na ordem (pedido do usuário). Mora fora da tela porque é decisão de DADO — o que vai ao
 * servidor, quem passa a ser obrigatório, quem pode entrar —, e a tela só arrasta.
 *
 * Gravadas, as duas são uma lista só, o identificador primeiro: é a ordem dos campos no item, e a
 * mesma em que a ficha e a grade já mostravam (`orderedProperties`).
 */
export type PropertyLayout = { identifier: IdeaProperty[], others: IdeaProperty[] }

export function layoutOf(properties: IdeaProperty[]): PropertyLayout {
    return {
        identifier: properties.filter(property => property.identifier),
        others: properties.filter(property => !property.identifier)
    }
}

/** O que vai ao `PATCH` da coleção: a ordem inteira e quem compõe o identificador. */
export function layoutRequest(layout: PropertyLayout): { propertyOrder: string[], identifiers: string[] } {
    const identifiers = layout.identifier.map(property => property.alias)
    return {propertyOrder: [...identifiers, ...layout.others.map(property => property.alias)], identifiers}
}

/** O arrasto mudou alguma coisa? Soltar onde estava não vai ao servidor. */
export function layoutChanged(layout: PropertyLayout, properties: IdeaProperty[]): boolean {
    return JSON.stringify(layoutRequest(layout)) !== JSON.stringify(layoutRequest(layoutOf(properties)))
}

/**
 * As propriedades como ficam, para a tela já mostrar antes de o servidor responder: cópias, com o
 * `identifier` de cada uma e o obrigatório de quem entrou sem ser — o original fica intacto, para
 * voltar se o servidor recusar. O calculado entra sem virar obrigatório: ninguém o preenche, e o
 * servidor também não o marca.
 */
export function propertiesOf(layout: PropertyLayout): IdeaProperty[] {
    const required = (property: IdeaProperty) => identifierBlocker(property) === 'not-required' || property.required
    return [
        ...layout.identifier.map(property => ({...property, identifier: true, required: required(property)})),
        ...layout.others.map(property => ({...property, identifier: false}))
    ]
}

/** Quem entrou no identificador sem ser obrigatório — e por isso a pergunta, antes de gravar. */
export function enteringNotRequired(layout: PropertyLayout): IdeaProperty | undefined {
    return layout.identifier.find(property => !property.identifier && identifierBlocker(property) === 'not-required')
}

/**
 * Por que a caixa do identificador recusa esta propriedade, ou null. O "não é obrigatório" não recusa:
 * vira a pergunta (`enteringNotRequired`).
 */
export function identifierDropBlocker(property: IdeaProperty): IdentifierBlock | null {
    const blocker = identifierBlocker(property)
    return blocker === 'not-required' ? null : blocker
}

/**
 * O nome dos itens montado na ordem da caixa — o mesmo separador do servidor. Sem campo na caixa, o
 * nome que o servidor dá (`Instance`): a coleção e o número — a caixa vazia também mostra como fica
 * (pedido do usuário).
 */
export function identifierPreview(layout: PropertyLayout, ideaName: string): string {
    if (!layout.identifier.length) return `${ideaName} #1`
    return layout.identifier.map(property => property.name).join(IDENTIFIER_SEPARATOR)
}
